import config from '../config/unified';

export interface SessionData {
  id: string;
  createdAt: string;
  lastActivity: string;
  expiresAt: string;
  metadata: SessionMetadata;
  events: any[];
}

export interface SessionMetadata {
  // Browser Information
  userAgent: string;
  language: string;
  
  // Device Information
  screenResolution: string;
  viewportSize: string;
  deviceType: 'desktop' | 'tablet' | 'mobile' | 'unknown';
  touchCapable: boolean;
  
  // Navigation Information
  referrer: string;
  landingPage: string;

  // Site/Brand Information (multi-site support)
  brand?: string;  // 'accs' | 'creditdirect' | 'unknown'

  // UTM Parameters
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmTerm?: string;
  utmContent?: string;
  
  // Timing Information
  timezone: string;
  sessionStartTime: number;
  pageLoadTime?: number;
}

export class SessionManager {
  private static readonly SESSION_KEY = 'webflow_session';
  private static readonly SESSION_TIMEOUT = 10 * 60 * 1000; // 10 minutes - shorter for fresh-per-visit model
  private static readonly GRACE_PERIOD = 2 * 60 * 1000; // 2 minutes grace period
  
  private currentSession: SessionData | null = null;
  private timeoutHandle: NodeJS.Timeout | null = null;
  private renewalInterval: NodeJS.Timeout | null = null;
  private wasRestoredFromStorage: boolean = false; // Track if session was restored vs newly created

  constructor() {
    this.initialize();
  }

  /**
   * Initialize session management
   * CRITICAL CHANGE: ALWAYS CREATE NEW SESSION - NO RESTORATION!
   */
  private initialize(): void {
    // BUSINESS REQUIREMENT: Every page visit = new lead opportunity
    // TECHNICAL SOLUTION: Always create fresh session, never restore
    
    if (config.debug) {
      console.log('🔥 SessionManager: ALWAYS creating fresh session (no restoration)');
    }
    
    // Clear any existing sessions from storage
    this.clearStoredSession();
    
    // ALWAYS create new session - never restore
    this.currentSession = this.createNewSession();
    this.wasRestoredFromStorage = false; // Never restored anymore
    
    if (config.debug) {
      console.log('📱 Created FRESH session:', this.currentSession.id);
    }
    
    // Set up automatic renewal and timeout (much shorter now)
    this.setupAutomaticRenewal();
    this.setupSessionTimeout();
    
    // Save session (but it will be cleared on next page load anyway)
    this.saveSession();
  }

  /**
   * Generate a new UUID v4
   */
  private generateUUID(): string {
    // Use crypto.randomUUID if available (modern browsers)
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return `sess_${crypto.randomUUID()}`;
    }
    
    // Fallback UUID v4 implementation
    const uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
    
    return `sess_${uuid}`;
  }

  /**
   * Create a new session with metadata
   */
  private createNewSession(): SessionData {
    const now = new Date().toISOString();
    const metadata = this.collectSessionMetadata();
    
    return {
      id: this.generateUUID(),
      createdAt: now,
      lastActivity: now,
      expiresAt: new Date(Date.now() + SessionManager.SESSION_TIMEOUT).toISOString(),
      metadata,
      events: []
    };
  }

  /**
   * Collect comprehensive session metadata
   */
  private collectSessionMetadata(): SessionMetadata {
    const urlParams = new URLSearchParams(window.location.search);
    
    return {
      // Browser Information
      userAgent: navigator.userAgent || '',
      language: navigator.language || 'unknown',
      
      // Device Information
      screenResolution: `${screen.width}x${screen.height}`,
      viewportSize: `${window.innerWidth}x${window.innerHeight}`,
      deviceType: this.detectDeviceType(),
      touchCapable: 'ontouchstart' in window || navigator.maxTouchPoints > 0,
      
      // Navigation Information
      referrer: document.referrer || '',
      landingPage: window.location.href,
      
      // UTM Parameters
      utmSource: urlParams.get('utm_source') || undefined,
      utmMedium: urlParams.get('utm_medium') || undefined,
      utmCampaign: urlParams.get('utm_campaign') || undefined,
      utmTerm: urlParams.get('utm_term') || undefined,
      utmContent: urlParams.get('utm_content') || undefined,
      
      // Timing Information
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'unknown',
      sessionStartTime: Date.now(),
      pageLoadTime: performance.timing ? 
        performance.timing.loadEventEnd - performance.timing.navigationStart : 
        undefined
    };
  }

  /**
   * Detect device type based on screen size and user agent
   */
  private detectDeviceType(): 'desktop' | 'tablet' | 'mobile' | 'unknown' {
    const width = window.innerWidth;
    const userAgent = navigator.userAgent.toLowerCase();
    
    if (userAgent.includes('mobile') || width < 768) {
      return 'mobile';
    } else if (userAgent.includes('tablet') || (width >= 768 && width < 1024)) {
      return 'tablet';
    } else if (width >= 1024) {
      return 'desktop';
    }
    
    return 'unknown';
  }

  /**
   * Check if session is still valid
   */
  private isSessionValid(session: SessionData): boolean {
    const now = Date.now();
    const expiresAt = new Date(session.expiresAt).getTime();
    const lastActivity = new Date(session.lastActivity).getTime();
    
    // Check hard expiry
    if (now > expiresAt) {
      return false;
    }
    
    // Check activity timeout
    if (now > (lastActivity + SessionManager.SESSION_TIMEOUT)) {
      return false;
    }
    
    return true;
  }

  /**
   * Update last activity timestamp
   */
  private updateLastActivity(): void {
    if (!this.currentSession) return;
    
    const now = new Date().toISOString();
    this.currentSession.lastActivity = now;
    this.currentSession.expiresAt = new Date(Date.now() + SessionManager.SESSION_TIMEOUT).toISOString();
    
    this.saveSession();
  }

  /**
   * Set up automatic session renewal on user activity
   */
  private setupAutomaticRenewal(): void {
    // Clear existing interval
    if (this.renewalInterval) {
      clearInterval(this.renewalInterval);
    }
    
    // Renew session every 5 minutes if there's activity
    this.renewalInterval = setInterval(() => {
      if (this.currentSession && this.isSessionValid(this.currentSession)) {
        this.updateLastActivity();
        if (config.debug) {
          console.log('📱 Session auto-renewed:', this.currentSession.id);
        }
      }
    }, 5 * 60 * 1000); // 5 minutes

    // Set up activity listeners for immediate renewal
    const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    let lastRenewal = Date.now();
    
    const handleActivity = () => {
      const now = Date.now();
      // Only renew if it's been at least 1 minute since last renewal
      if (now - lastRenewal > 60 * 1000) {
        this.updateLastActivity();
        lastRenewal = now;
      }
    };
    
    activityEvents.forEach(event => {
      document.addEventListener(event, handleActivity, { passive: true });
    });
  }

  /**
   * Set up session timeout handling
   */
  private setupSessionTimeout(): void {
    // Clear existing timeout
    if (this.timeoutHandle) {
      clearTimeout(this.timeoutHandle);
    }
    
    if (!this.currentSession) return;
    
    const expiresAt = new Date(this.currentSession.expiresAt).getTime();
    const timeUntilExpiry = expiresAt - Date.now();
    
    if (timeUntilExpiry > 0) {
      this.timeoutHandle = setTimeout(() => {
        this.handleSessionTimeout();
      }, timeUntilExpiry);
    }
  }

  /**
   * Handle session timeout
   */
  private handleSessionTimeout(): void {
    if (config.debug) {
      console.log('📱 Session timed out:', this.currentSession?.id);
    }
    
    // End current session
    this.endSession('timeout');
    
    // Create new session
    this.currentSession = this.createNewSession();
    this.saveSession();
    this.setupAutomaticRenewal();
    this.setupSessionTimeout();
    
    if (config.debug) {
      console.log('📱 New session created after timeout:', this.currentSession.id);
    }
  }

  /**
   * Load session from storage
   */
  private loadSession(): SessionData | null {
    try {
      // Try sessionStorage first (tab-specific)
      let sessionStr = sessionStorage.getItem(SessionManager.SESSION_KEY);
      
      if (!sessionStr) {
        // Fallback to localStorage (persistent across tabs)
        sessionStr = localStorage.getItem(SessionManager.SESSION_KEY);
      }
      
      if (sessionStr) {
        return JSON.parse(sessionStr) as SessionData;
      }
    } catch (error) {
      if (config.debug) {
        console.error('Error loading session from storage:', error);
      }
    }
    
    return null;
  }

  /**
   * Save session to storage
   */
  private saveSession(): void {
    if (!this.currentSession) return;
    
    try {
      const sessionStr = JSON.stringify(this.currentSession);
      
      // Save to both storages
      sessionStorage.setItem(SessionManager.SESSION_KEY, sessionStr);
      localStorage.setItem(SessionManager.SESSION_KEY, sessionStr);
    } catch (error) {
      if (config.debug) {
        console.error('Error saving session to storage:', error);
      }
    }
  }

  /**
   * Clear session from storage
   */
  private clearStoredSession(): void {
    try {
      sessionStorage.removeItem(SessionManager.SESSION_KEY);
      localStorage.removeItem(SessionManager.SESSION_KEY);
    } catch (error) {
      if (config.debug) {
        console.error('Error clearing session from storage:', error);
      }
    }
  }

  /**
   * End the current session
   */
  private endSession(reason: 'timeout' | 'explicit' | 'completed'): void {
    if (this.currentSession && config.debug) {
      console.log(`📱 Session ended (${reason}):`, this.currentSession.id);
    }
    
    // Clear timers
    if (this.timeoutHandle) {
      clearTimeout(this.timeoutHandle);
      this.timeoutHandle = null;
    }
    
    if (this.renewalInterval) {
      clearInterval(this.renewalInterval);
      this.renewalInterval = null;
    }
    
    // Clear storage
    this.clearStoredSession();
  }

  // Public methods

  /**
   * Get current session ID
   */
  public getSessionId(): string {
    return this.currentSession?.id || '';
  }

  /**
   * Get current session data
   */
  public getSession(): SessionData | null {
    return this.currentSession;
  }

  /**
   * Get session metadata
   */
  public getMetadata(): SessionMetadata | null {
    return this.currentSession?.metadata || null;
  }

  /**
   * Add event to current session
   */
  public addEvent(event: any): void {
    if (!this.currentSession) return;
    
    this.currentSession.events.push({
      ...event,
      timestamp: new Date().toISOString(),
      sessionId: this.currentSession.id
    });
    
    this.updateLastActivity();
  }

  /**
   * Get session events
   */
  public getEvents(): any[] {
    return this.currentSession?.events || [];
  }

  /**
   * Manually renew session
   */
  public renewSession(): void {
    this.updateLastActivity();
    this.setupSessionTimeout();
  }

  /**
   * Manually end session
   */
  public destroy(): void {
    this.endSession('explicit');
    this.currentSession = null;
  }

  /**
   * Get session duration in milliseconds
   */
  public getSessionDuration(): number {
    if (!this.currentSession) return 0;
    
    const createdAt = new Date(this.currentSession.createdAt).getTime();
    return Date.now() - createdAt;
  }

  /**
   * Get time until session expiry in milliseconds
   */
  public getTimeUntilExpiry(): number {
    if (!this.currentSession) return 0;
    
    const expiresAt = new Date(this.currentSession.expiresAt).getTime();
    return Math.max(0, expiresAt - Date.now());
  }

  /**
   * Check if session is active
   */
  public isActive(): boolean {
    return this.currentSession !== null && this.isSessionValid(this.currentSession);
  }

  /**
   * Check if current session was restored from storage (vs newly created)
   * ALWAYS RETURNS FALSE - we never restore sessions anymore
   */
  public wasRestored(): boolean {
    return false; // Sessions are ALWAYS fresh now
  }
}